import { useState, useEffect } from "react";
import {
  Plus, Trash2, X, CheckCircle, AlertCircle, Dumbbell,
  Building2, RefreshCw, Shield, User, Pencil, Edit2
} from "lucide-react";
import TemplateEditorModal from "./TemplateEditorModal";
import SporterSchemaEditModal from "./SporterSchemaEditModal";

const API = process.env.REACT_APP_BACKEND_URL;

const NIVEAUS = ["Beginner", "Gemiddeld", "Gevorderd", "Elite"];

function TemplateForm({ onSave, onClose }) {
  const [naam, setNaam] = useState("");
  const [beschrijving, setBeschrijving] = useState("");
  const [niveau, setNiveau] = useState("Beginner");
  const [trainingsdagen, setTrainingsdagen] = useState(3);
  const [weken, setWeken] = useState(6);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!naam.trim()) { setError("Naam is verplicht"); return; }
    setLoading(true); setError("");
    const ok = await onSave({ naam, beschrijving, niveau, trainingsdagen, weken, dagen: [] });
    if (!ok) setError("Aanmaken mislukt");
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="flex flex-col gap-1.5 sm:col-span-2">
          <span className="text-zinc-400 text-xs font-bold uppercase tracking-widest">Naam template *</span>
          <input
            value={naam}
            onChange={e => setNaam(e.target.value)}
            placeholder="bijv. Full Body Basis 6 Weken"
            className="bg-[#1a1a1a] border border-white/8 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50"
          />
        </label>
        <label className="flex flex-col gap-1.5 sm:col-span-2">
          <span className="text-zinc-400 text-xs font-bold uppercase tracking-widest">Beschrijving</span>
          <textarea
            value={beschrijving}
            onChange={e => setBeschrijving(e.target.value)}
            placeholder="Korte omschrijving van het schema..."
            rows={2}
            className="bg-[#1a1a1a] border border-white/8 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50 resize-none"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-zinc-400 text-xs font-bold uppercase tracking-widest">Niveau</span>
          <select
            value={niveau}
            onChange={e => setNiveau(e.target.value)}
            className="bg-[#1a1a1a] border border-white/8 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50"
          >
            {NIVEAUS.map(n => <option key={n}>{n}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-zinc-400 text-xs font-bold uppercase tracking-widest">Trainingsdagen/week</span>
          <input
            type="number"
            min={1} max={7}
            value={trainingsdagen}
            onChange={e => setTrainingsdagen(+e.target.value)}
            className="bg-[#1a1a1a] border border-white/8 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-zinc-400 text-xs font-bold uppercase tracking-widest">Aantal weken</span>
          <input
            type="number"
            min={1} max={52}
            value={weken}
            onChange={e => setWeken(+e.target.value)}
            className="bg-[#1a1a1a] border border-white/8 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50"
          />
        </label>
      </div>
      {error && (
        <div className="flex items-center gap-2 text-red-400 text-sm">
          <AlertCircle size={13} /> {error}
        </div>
      )}
      <div className="flex gap-3">
        <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-white/8 text-zinc-400 text-sm hover:text-white">
          Annuleren
        </button>
        <button type="submit" disabled={loading} className="flex-1 py-2.5 rounded-xl bg-[#C9A84C] text-black font-bold text-sm hover:bg-[#d4b05c] disabled:opacity-60">
          {loading ? "Aanmaken..." : "Template aanmaken"}
        </button>
      </div>
    </form>
  );
}

// ── MODAL: Aan sporter toevoegen ──────────────────────────────────────────────
function AanSporterModal({ template, token, onClose }) {
  const [sporters,    setSporters]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [bezig,       setBezig]       = useState(null);
  const [resultaat,   setResultaat]   = useState(null);
  const [bewerkData,  setBewerkData]  = useState(null); // { schemaId, sporter }

  useEffect(() => {
    fetch(`${API}/api/admin/own-sporters`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { setSporters(d.sporters || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  const toewijzen = async (sporter) => {
    setBezig(sporter.id);
    setResultaat(null);
    try {
      const r = await fetch(`${API}/api/admin/schema-templates/${template.id}/duplicate-for-sporter`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: sporter.id }),
      });
      const d = await r.json();
      if (d.ok) {
        setResultaat({ ok: true, msg: `Schema toegevoegd aan ${sporter.naam}!`, schemaId: d.schema_id, sporter });
      } else {
        setResultaat({ ok: false, msg: d.detail || "Mislukt" });
      }
    } catch {
      setResultaat({ ok: false, msg: "Fout bij toevoegen" });
    }
    setBezig(null);
  };

  // Bewerk-editor staat open
  if (bewerkData) {
    return (
      <SporterSchemaEditModal
        schemaId={bewerkData.schemaId}
        sporter={bewerkData.sporter}
        token={token}
        onClose={() => { setBewerkData(null); onClose(); }}
        onSaved={() => { setBewerkData(null); onClose(); }}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#111] border border-white/10 rounded-2xl p-6 max-w-md w-full animate-fadeIn">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-white font-bold">Aan eigen sporter toevoegen</h3>
            <p className="text-zinc-500 text-xs mt-0.5 truncate max-w-[280px]">{template.naam}</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>

        {resultaat && (
          <div className={`mb-3 p-3 rounded-xl text-sm font-semibold ${resultaat.ok ? "bg-green-500/10 text-green-400 border border-green-500/20" : "bg-red-500/10 text-red-400 border border-red-500/20"}`}>
            <p>{resultaat.msg}</p>
            {resultaat.ok && resultaat.schemaId && (
              <button
                data-testid="bewerk-na-toewijzing-btn"
                onClick={() => setBewerkData({ schemaId: resultaat.schemaId, sporter: resultaat.sporter })}
                className="mt-2 flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg transition-colors"
                style={{ background: "#C9A84C20", color: "#C9A84C", border: "1px solid #C9A84C40" }}
              >
                <Edit2 size={11} /> Bewerk schema voor {resultaat.sporter.naam}
              </button>
            )}
          </div>
        )}

        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {loading && (
            <p className="text-zinc-600 text-sm text-center py-4">Laden...</p>
          )}
          {!loading && sporters.length === 0 && (
            <p className="text-zinc-600 text-sm text-center py-4">Geen sporters met schema-toegang gevonden</p>
          )}
          {sporters.map(s => (
            <div key={s.id} className="flex items-center justify-between p-3 rounded-xl border border-white/6 bg-[#1a1a1a] hover:border-white/15 transition-all">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black text-black"
                  style={{ background: "#C9A84C" }}>
                  {(s.naam || "?")[0].toUpperCase()}
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">{s.naam}</p>
                  <p className="text-zinc-600 text-xs">{s.email}</p>
                </div>
              </div>
              <button
                data-testid={`toewijzen-aan-sporter-${s.id}`}
                onClick={() => toewijzen(s)}
                disabled={!!bezig}
                className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all disabled:opacity-40"
                style={{ background: "#C9A84C15", color: "#C9A84C", border: "1px solid #C9A84C30" }}
              >
                {bezig === s.id ? "..." : "Toevoegen"}
              </button>
            </div>
          ))}
        </div>

        <p className="text-zinc-700 text-xs text-center mt-3">
          Elk schema is een persoonlijke kopie die je apart kunt bewerken.
        </p>
        <button onClick={onClose} className="w-full mt-3 py-2.5 rounded-xl bg-white/6 text-white text-sm font-semibold hover:bg-white/10 transition-all">
          Sluiten
        </button>
      </div>
    </div>
  );
}

// ── MODAL: Template toewijzen aan bedrijf ─────────────────────────────────────
function AssignModal({ template, companies, token, onClose }) {
  const [assigned, setAssigned] = useState(template.assigned_companies || []);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const toggle = async (companyId) => {
    setSaving(true); setResult(null);
    const isAssigned = assigned.includes(companyId);
    try {
      if (isAssigned) {
        await fetch(`${API}/api/admin/schema-templates/${template.id}/assign/${companyId}`, { method: "DELETE", ...auth });
        setAssigned(a => a.filter(id => id !== companyId));
      } else {
        await fetch(`${API}/api/admin/schema-templates/${template.id}/assign`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...auth.headers },
          body: JSON.stringify({ company_ids: [companyId] }),
        });
        setAssigned(a => [...a, companyId]);
      }
      setResult({ ok: true, msg: isAssigned ? "Ingetrokken" : "Toegewezen!" });
    } catch {
      setResult({ ok: false, msg: "Fout" });
    }
    setSaving(false);
    setTimeout(() => setResult(null), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#111] border border-white/10 rounded-2xl p-6 max-w-md w-full animate-fadeIn">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-white font-bold">Template toewijzen</h3>
            <p className="text-zinc-500 text-xs mt-0.5">{template.naam}</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>

        {result && (
          <div className={`mb-3 p-2.5 rounded-xl text-xs ${result.ok ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}>
            {result.msg}
          </div>
        )}

        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {companies.length === 0 && (
            <p className="text-zinc-600 text-sm text-center py-4">Geen bedrijven gevonden</p>
          )}
          {companies.map(c => {
            const isAssigned = assigned.includes(c.id);
            return (
              <div
                key={c.id}
                className={`flex items-center justify-between p-3 rounded-xl border transition-all cursor-pointer ${
                  isAssigned ? "border-[#C9A84C]/40 bg-[#C9A84C]/5" : "border-white/6 bg-[#1a1a1a] hover:border-white/15"
                }`}
                onClick={() => !saving && toggle(c.id)}
              >
                <div className="flex items-center gap-2.5">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${isAssigned ? "bg-[#C9A84C]/20 text-[#C9A84C]" : "bg-zinc-800 text-zinc-400"}`}>
                    {c.naam?.charAt(0)?.toUpperCase()}
                  </div>
                  <span className="text-white text-sm font-medium">{c.naam}</span>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${isAssigned ? "bg-[#C9A84C] border-[#C9A84C]" : "border-white/20"}`}>
                  {isAssigned && <CheckCircle size={10} className="text-black" />}
                </div>
              </div>
            );
          })}
        </div>

        <button onClick={onClose} className="w-full mt-4 py-2.5 rounded-xl bg-white/6 text-white text-sm font-semibold hover:bg-white/10 transition-all">
          Sluiten
        </button>
      </div>
    </div>
  );
}

export default function SchemaTemplatesTab({ token }) {
  const [templates, setTemplates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [assignModal, setAssignModal] = useState(null);
  const [sporterModal, setSporterModal] = useState(null);
  const [editorModal, setEditorModal] = useState(null); // template om te bewerken
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    const authH = { Authorization: `Bearer ${token}` };
    Promise.all([
      fetch(`${API}/api/admin/schema-templates`, { headers: authH }).then(r => r.json()).catch(() => []),
      fetch(`${API}/api/companies`, { headers: authH }).then(r => r.json()).catch(() => ({})),
    ]).then(([tmpl, comp]) => {
      setTemplates(Array.isArray(tmpl) ? tmpl : []);
      setCompanies(Array.isArray(comp) ? comp : (comp?.companies || []));
      setLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const load = () => {
    if (!token) return;
    setLoading(true);
    const authH = { Authorization: `Bearer ${token}` };
    Promise.all([
      fetch(`${API}/api/admin/schema-templates`, { headers: authH }).then(r => r.json()).catch(() => []),
      fetch(`${API}/api/companies`, { headers: authH }).then(r => r.json()).catch(() => ({})),
    ]).then(([tmpl, comp]) => {
      setTemplates(Array.isArray(tmpl) ? tmpl : []);
      setCompanies(Array.isArray(comp) ? comp : (comp?.companies || []));
      setLoading(false);
    });
  };

  const handleCreate = async (data) => {
    try {
      const r = await fetch(`${API}/api/admin/schema-templates`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(data),
      });
      if (!r.ok) return false;
      setToast("Template aangemaakt!");
      setShowForm(false);
      load();
      setTimeout(() => setToast(null), 3000);
      return true;
    } catch { return false; }
  };

  const handleDelete = async (id, naam) => {
    if (!window.confirm(`Template "${naam}" verwijderen?`)) return;
    await fetch(`${API}/api/admin/schema-templates/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    load();
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="w-6 h-6 border-2 border-[#C9A84C]/30 border-t-[#C9A84C] rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">
      {assignModal && (
        <AssignModal
          template={assignModal}
          companies={companies}
          token={token}
          onClose={() => { setAssignModal(null); load(); }}
        />
      )}

      {sporterModal && (
        <AanSporterModal
          template={sporterModal}
          token={token}
          onClose={() => setSporterModal(null)}
        />
      )}

      {editorModal && (
        <TemplateEditorModal
          templateId={editorModal.id}
          token={token}
          onClose={() => setEditorModal(null)}
          onSaved={() => { setEditorModal(null); load(); setToast("Template bijgewerkt!"); setTimeout(() => setToast(null), 3000); }}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl bg-[#111] border border-[#C9A84C]/40 text-white text-sm font-semibold shadow-2xl flex items-center gap-2">
          <CheckCircle size={14} className="text-[#C9A84C]" /> {toast}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white font-bold text-xl">Schema Templates</h2>
          <p className="text-zinc-500 text-sm mt-1">
            Maak schema templates aan en wijs ze toe aan specifieke bedrijven.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-1.5 text-zinc-500 hover:text-white text-sm border border-white/8 px-3 py-1.5 rounded-lg transition-colors">
            <RefreshCw size={12} /> Vernieuwen
          </button>
          <button
            onClick={() => setShowForm(f => !f)}
            data-testid="new-template-btn"
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#C9A84C] text-black text-sm font-bold hover:bg-[#d4b05c] transition-all"
          >
            <Plus size={14} /> Nieuwe template
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bg-[#111] border border-[#C9A84C]/25 rounded-2xl p-6 animate-fadeIn">
          <h3 className="text-white font-semibold mb-4">Nieuwe schema template</h3>
          <TemplateForm onSave={handleCreate} onClose={() => setShowForm(false)} />
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
            style={{ borderColor: "#C9A84C", borderTopColor: "transparent" }} />
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-14 h-14 rounded-2xl bg-[#1a1a1a] border border-white/6 flex items-center justify-center mx-auto mb-4">
            <Dumbbell size={22} className="text-zinc-600" />
          </div>
          <h3 className="text-white font-semibold mb-2">Nog geen templates</h3>
          <p className="text-zinc-500 text-sm">Maak je eerste schema template aan en wijs hem toe aan bedrijven.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map(t => (
            <div key={t.id} className="bg-[#111] border border-white/8 rounded-2xl p-5 hover:border-white/15 transition-all">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-[#C9A84C]/10 border border-[#C9A84C]/20 flex items-center justify-center shrink-0">
                    <Dumbbell size={16} className="text-[#C9A84C]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-white font-bold">{t.naam}</h3>
                    {t.beschrijving && (
                      <p className="text-zinc-500 text-xs mt-0.5 truncate">{t.beschrijving}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-zinc-500 text-xs">{t.niveau}</span>
                      <span className="text-zinc-700">·</span>
                      <span className="text-zinc-500 text-xs">{t.trainingsdagen} dagen/week</span>
                      <span className="text-zinc-700">·</span>
                      <span className="text-zinc-500 text-xs">{t.weken} weken</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-zinc-800/50 text-zinc-400 text-xs">
                    <Building2 size={10} />
                    {t.assigned_companies?.length || 0} bedrijven
                  </div>
                  <button
                    onClick={() => setEditorModal(t)}
                    data-testid={`edit-template-${t.id}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-zinc-300 text-xs font-bold hover:bg-white/10 transition-all"
                  >
                    <Pencil size={11} /> Bewerken
                  </button>
                  <button
                    onClick={() => setAssignModal(t)}
                    data-testid={`assign-template-${t.id}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#C9A84C]/10 border border-[#C9A84C]/25 text-[#C9A84C] text-xs font-bold hover:bg-[#C9A84C]/20 transition-all"
                  >
                    <Shield size={11} /> Toewijzen
                  </button>
                  <button
                    onClick={() => setSporterModal(t)}
                    data-testid={`aan-sporter-template-${t.id}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-zinc-300 text-xs font-bold hover:bg-white/10 transition-all"
                  >
                    <User size={11} /> Aan sporter
                  </button>
                  <button
                    onClick={() => handleDelete(t.id, t.naam)}
                    data-testid={`delete-template-${t.id}`}
                    className="p-2 rounded-lg text-zinc-600 hover:text-red-400 hover:bg-red-500/10 transition-all"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
